import {
  EDatasourceType,
  ESdChangeReason,
  ICreateLibraryRequest,
  IDatasourceDto,
  IUpdateLibraryRequest,
  WarningPanelType
} from "@kuika/kuika-cl-model";
import { PackageType } from "@kuika/kuika-cl-model/src/models/package-management";
import Editor, { Monaco } from "@monaco-editor/react";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import { HttpStatusCode } from "axios";
import { debounce } from "lodash";
import * as monacoEditor from "monaco-editor";
import * as monaco from "monaco-editor/esm/vs/editor/editor.api";
import ObjectHash from "object-hash";
import "monaco-editor/min/vs/editor/editor.main.css";
import { ValidateErrorEntity } from "rc-field-form/es/interface";
import * as React from "react";
import { connect } from "react-redux";
import { createHighlighter, Highlighter } from "shiki";
import { DatasourceService } from "../../../services/datasource-service";
import { CompletionItemKind, LSPDiagnostic, LanguageServerService } from "../../../services/language-server-service";
import { PackageManagementService } from "../../../services/package-management-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { csharpEditorOptions } from "../../../shared/utility/monaco-editor-config";
import { injectMonacoStyleFixes } from "../../../shared/utility/monaco-style-fixes";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import { AICodeGenerator, EAICodeGeneratorType } from "../../common/ai/ai-code-generator";
import { IPlannedCodeInsertion } from "../../common/ai/ai-code-insertion";
import { AiPendingEdit } from "../../common/ai/ai-pending-edit";
import { AiPendingEditBar } from "../../common/ai/ai-pending-edit-bar";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import ExternalLibrariesButton from "../../common/external-libraries-button/external-libraries-button";
import { ITestRunnerMethod, TestRunner } from "../../common/test-runner/test-runner";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import { CodeEditorControls, ICodeEditorControlsState } from "../code-editor-shell/code-editor-controls";
import { CodeEditorHeader } from "../code-editor-shell/code-editor-header";
import {
  codeEditorRenameTestIds,
  getOpeningDatasourceName,
  validateRenameField
} from "../code-editor-shell/code-editor-rename";
import { CodeEditorRenameTitle } from "../code-editor-shell/code-editor-rename-title";
import { CodeEditorSidePanel, ECodeEditorSideTab } from "../code-editor-shell/code-editor-side-panel";
import { CodeEditorSplitter } from "../code-editor-shell/code-editor-splitter";
import { installShikiThemes, resolveAppTheme, TAppTheme, watchAppTheme } from "../code-editor-shell/code-editor-theme";
import { CodeEditorToolbar } from "../code-editor-shell/code-editor-toolbar";
import { getCSharpTemplateOptionsFor } from "./csharp-templates";
import { requestDatasourceReveal } from "./datasource-reveal";
import {
  CSHARP_EDITOR_DATASOURCE_TYPES,
  CodeDatasourceSwitcherChip
} from "./editor-switcher/code-datasource-switcher-chip";

// Lazy app-lifetime Shiki highlighter. shikiToMonaco registers a GLOBAL Monaco tokens provider
// that closes over the highlighter, so a per-mount instance disposed on unmount left Monaco
// holding a dead reference — the next tokenization (e.g. after a hot-reload remount) threw an
// uncaught "Shiki instance has been disposed". One shared never-disposed instance is the usage
// shikiToMonaco is designed for, and reuse also removes the old one-instance-per-open leak.
let sharedCSharpHighlighter: Promise<Highlighter> | null = null;
const getCSharpHighlighter = (): Promise<Highlighter> => {
  if (!sharedCSharpHighlighter) {
    sharedCSharpHighlighter = createHighlighter({
      // Both, so the editor can follow the app's appearance: it used to be pinned to
      // `light-plus`, which is a white code surface on the dark theme.
      themes: ["light-plus", "dark-plus"],
      langs: ["csharp"]
    });
  }
  return sharedCSharpHighlighter;
};

const fadeInScaleKeyframes = `
@keyframes fadeInScale {
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeOutScale {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.6);
  }
}

@keyframes containerGrow {
  0% {
    width: 0;
  }
  100% {
    width: 16px;
  }
}

@keyframes containerShrink {
  0% {
    width: 16px;
  }
  100% {
    width: 0;
  }
}
`;

const styleSheet = document.createElement("style");
styleSheet.textContent = fadeInScaleKeyframes;
document.head.appendChild(styleSheet);

export interface ICreateConnectionCsharpState {
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  isTemplateSelected: boolean;
  selectedTemplate?: EDatasourceType;
  /**
   * The committed name. The field mounts only while renaming, and the form hands back mounted
   * fields alone — so this, never the form, is what the hash, the save and the dialogs read.
   */
  datasourceName: string;
  isNameOnEdit: boolean;
  /** The template name when the picker has to cut it, so the tooltip only appears then. */
  truncatedTemplateLabel: string;
  submitButtonDisabledStatus: boolean;
  /** Undo, wrapping, the font stepper, the caret and the markers — owned by `CodeEditorControls`. */
  controls: ICodeEditorControlsState;
  /** Which side tab is showing; Kuika AI is the only one this editor offers. */
  sideTab: ECodeEditorSideTab;
  /** Shiki's own theme, picked from the app's appearance. */
  shikiTheme: string;
  /** Folded from the vertical grip, and dragged by it. */
  isSidePanelOpened: boolean;
  sidePanelWidth: number;
  selectedPunto: number;
  diagnostics: LSPDiagnostic[];
  lastCodeActions: any[];
  lastCodeContent: string;
  /** The AI edit waiting for the user's confirmation; the editor still holds their own code. */
  pendingAiEdit: IPlannedCodeInsertion | null;
  externalLibraries: { name: string; version: string }[];
  librarySearchQuery: string;
  libraryView: "installed" | "new";
  nugetPackages: Array<{ id: string; version: string; description: string }>;
  isLoadingNugetPackages: boolean;
  nugetSearchQuery: string;
  nugetPagination: {
    skip: number;
    take: number;
    total: number;
  };
  installedLibraries: Array<{ id: string; name: string; version: string; description?: string }>;
  isLoadingInstalledLibraries: boolean;
  activePackageLoading?: string;
  expandedLibrary?: string;
  libraryVersions: { [packageId: string]: Array<{ version: string }> };
  isLoadingVersions: boolean;
}

export interface ICreateConnectionCsharpProps {
  editingDatasource?: IDatasourceDto;
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

interface ICsharpActionFormValues {
  DatasourceName: string;
  CSharpCode: string;
}

// Stable reference for the "no versions" case so the popover's version-loading effect isn't retriggered every render.
const EMPTY_VERSIONS: never[] = [];

class CreateConnectionCsharp extends PureSdComponent<
  ICreateConnectionCsharpProps & IWithSDProps,
  ICreateConnectionCsharpState
> {
  formRef = React.createRef<FormInstance>();

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  editorRef: monacoEditor.editor.IStandaloneCodeEditor | null = null;

  aiPendingEdit: AiPendingEdit = new AiPendingEdit({
    onChange: (pendingAiEdit: IPlannedCodeInsertion | null) => this.setState({ pendingAiEdit })
  });

  controls: CodeEditorControls = new CodeEditorControls({
    onChange: (controls: ICodeEditorControlsState) => this.setState({ controls })
  });

  codeThemeObserver: MutationObserver | undefined;

  editorContainerRef = React.createRef<HTMLDivElement>();

  templateSelectRef: React.RefObject<HTMLSpanElement> = React.createRef<HTMLSpanElement>();

  lastClickedRow: number | null = null;

  completionProviderDisposable: monacoEditor.IDisposable | null = null;

  hoverProviderDisposable: monacoEditor.IDisposable | null = null;

  codeActionProviderDisposable: monacoEditor.IDisposable | null = null;

  definitionProviderDisposable: monacoEditor.IDisposable | null = null;

  diagnosticsAbortController: AbortController | null = null;

  codeActionsAbortController: AbortController | null = null;

  // Add cache for hover and definition
  private static hoverCache: Map<string, { info: any; timestamp: number }> = new Map();

  private static definitionCache: Map<string, { info: any; timestamp: number }> = new Map();

  private lastDefinitionRequest: { position: any; promise: Promise<any> } | null = null;

  private static readonly DEFINITION_DEBOUNCE = 300; // 300ms debounce

  private highlighter: Highlighter | null = null;

  lastCodeActions: any[] = [];

  lastCodeContent: string = "";

  constructor(props: ICreateConnectionCsharpProps & IWithSDProps) {
    super(props);
    const datasourceName: string = getOpeningDatasourceName(
      props.editingDatasource,
      props.sd.activeCreatingDatasourceName
    );
    this.state = {
      saveStatus: "idle",
      isTemplateSelected: false,
      selectedTemplate: undefined,
      datasourceName,
      // The create picker asks for the name first, so a new file opens named; only a file
      // with no name at all opens with the field.
      isNameOnEdit: !datasourceName,
      truncatedTemplateLabel: "",
      submitButtonDisabledStatus: true,
      controls: this.controls.state,
      sideTab: "coach",
      shikiTheme: resolveAppTheme() === "dark" ? "dark-plus" : "light-plus",
      isSidePanelOpened: true,
      sidePanelWidth: 288,
      selectedPunto: 12,
      diagnostics: [],
      lastCodeActions: [],
      lastCodeContent: "",
      pendingAiEdit: null,
      externalLibraries: [],
      librarySearchQuery: "",
      libraryView: "installed",
      nugetPackages: [],
      isLoadingNugetPackages: false,
      nugetSearchQuery: "",
      nugetPagination: {
        skip: 0,
        take: 20,
        total: 0
      },
      installedLibraries: [],
      isLoadingInstalledLibraries: false,
      activePackageLoading: undefined,
      libraryVersions: {},
      isLoadingVersions: false
    };
  }

  componentDidUpdate(prevProps: ICreateConnectionCsharpProps & IWithSDProps, prevState: ICreateConnectionCsharpState) {
    const fieldsValue = this.formRef.current?.getFieldsValue();
    if (prevProps.editingDatasource != this.props.editingDatasource && !this.props.editingDatasource) {
      fieldsValue.DatasourceName = "";
      fieldsValue.CSharpCode = ``;
      // The baseline from the values just written, not from the state: the name's setState
      // below has not landed yet, and a baseline read from it could never be matched again.
      this.objectHash = this.getObjectHash("", fieldsValue.CSharpCode);
      this.setState({
        isTemplateSelected: false,
        selectedTemplate: EDatasourceType.CSharpFunctions,
        datasourceName: "",
        isNameOnEdit: true
      });
    } else if (this.props.editingDatasource && prevProps.editingDatasource != this.props.editingDatasource) {
      const isMainAI = KMainFunctions.isMainAI();
      let code = this.props.editingDatasource?.code;
      if (isMainAI && code) {
        code = code.replace(/Kuika\.ThirdPartyApisCollection/g, "MainAI.ThirdPartyApisCollection");
      }
      const name: string = this.props.editingDatasource.name ?? "";
      fieldsValue.DatasourceName = name;
      fieldsValue.CSharpCode = code;
      this.objectHash = this.getObjectHash(name, code);
      this.setState({
        selectedTemplate: this.props.editingDatasource?.datasourceType,
        datasourceName: name,
        isNameOnEdit: !name
      });
    }
    this.formRef.current?.setFieldsValue(fieldsValue);
    if (
      !this.props.editingDatasource &&
      this.props.sd.activeCreatingDatasourceName &&
      prevProps.sd.activeCreatingDatasourceName === undefined
    ) {
      const name: string = this.props.sd.activeCreatingDatasourceName;
      fieldsValue.DatasourceName = name;
      let csharpCode = this.props.sd.activeCreatingCSharpCode ?? fieldsValue.CSharpCode ?? "";
      if (csharpCode.includes("NewClassName")) {
        const className =
          this.props.sd.activeCreatingDatasourceName.charAt(0).toUpperCase() +
          this.props.sd.activeCreatingDatasourceName.slice(1);
        csharpCode = csharpCode.replace(/NewClassName/g, className);
      }
      fieldsValue.CSharpCode = csharpCode;
      this.formRef.current!.setFieldsValue(fieldsValue);
      this.objectHash = this.getObjectHash(name, csharpCode);
      this.setState({
        selectedTemplate: this.props.sd.activeCreatingCSharpType ?? EDatasourceType.CSharpFunctions,
        isTemplateSelected: true,
        datasourceName: name,
        isNameOnEdit: false
      });
    }

    // The tick the request ARRIVES on, not the one after: reading `prevProps` meant the
    // editor ignored it once and then acted on it again after `closeDataSource` had already
    // cleared it, which closed whatever had just been opened.
    if (this.props.nextCallback && !prevProps.nextCallback) {
      this.handleCloseDatasource();
    }
  }

  async componentDidMount() {
    this.highlighter = await getCSharpHighlighter();

    (window as any).editorForm = this.formRef.current;

    let formValues = {};
    const { editingDatasource } = this.props;
    if (this.formRef.current && editingDatasource) {
      formValues = {
        CSharpCode: editingDatasource?.code,
        DatasourceName: editingDatasource?.name
      };
      this.formRef.current!.setFieldsValue(formValues);
      // The constructor has already opened on this name; the baseline says so explicitly.
      this.objectHash = this.getObjectHash(editingDatasource.name ?? "", editingDatasource.code);
      this.setState({ selectedTemplate: editingDatasource.datasourceType });
    }
    // activeCreatingDatasourceName can be stale here: cancelCreatingOrEditingDatasource can't clear
    // it (setActiveCreatingDatasourceName writes it, then cancels), so only read it while creating.
    if (!editingDatasource && this.props.sd.activeCreatingDatasourceName) {
      formValues = {
        DatasourceName: this.props.sd.activeCreatingDatasourceName,
        CSharpCode: this.props.sd.activeCreatingCSharpCode
      };
      this.formRef.current!.setFieldsValue(formValues);
      this.objectHash = this.getObjectHash(
        this.props.sd.activeCreatingDatasourceName,
        this.props.sd.activeCreatingCSharpCode
      );
      this.setState({
        selectedTemplate: this.props.sd.activeCreatingCSharpType ?? EDatasourceType.CSharpFunctions,
        isTemplateSelected: true
      });
      if (!this.props.sd.activeCreatingCSharpCode) {
        const response = await DatasourceService.GetCSharpCodeTemplates(
          EDatasourceType.CSharpFunctions,
          this.props.sd.activeCreatingDatasourceName
        );
        if (response.status === HttpStatusCode.Ok) {
          const code = response.data;
          this.props.sd.setActiveCreatingCSharpCode(code, EDatasourceType.CSharpFunctions);
          this.setState({ selectedTemplate: EDatasourceType.CSharpFunctions });
        }
      }
    }
  }

  nameValidator = async (_rule, value) => {
    if (!value) return;

    const existingScreenName = this.props.sd?.screenList?.find(
      (screen) => screen.name.toLowerCase() === value.toLowerCase()
    );
    const existingWFName = this.props.sd?.workFlowList?.find((wf) => wf.name.toLowerCase() === value.toLowerCase());

    if (existingScreenName || existingWFName)
      throw new Error("Datasource name can not be equal to screen name or Process name.");

    const existingDsName = this.props.sd?.datasources?.find(
      (ds) => ds.name.toLowerCase() === value.toLowerCase() && ds.id !== this.props.editingDatasource?.id
    );

    if (existingDsName) throw new Error("Datasource name already exist.");
  };

  namespaceValidator = async (_rule, value) => {
    if (!value.match(/^\s*namespace\s+Kuika\.ThirdPartyApisCollection\s*$/gm))
      throw new Error('Namespace must be "Kuika.ThirdPartyApisCollection"');
  };

  saveDatasource = async (): Promise<void> => {
    // The submit button is live again as soon as the ✓ replaces the spinner, so a second Enter
    // would re-fire the save behind the closing editor.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    const name: string | undefined = await this.resolveNameForSave();
    if (!name) {
      this.saveStatus.reset();
      return;
    }
    const form = this.formRef.current!.getFieldsValue();
    const ds = {
      name,
      datasourceType:
        this.state.selectedTemplate ?? this.props.editingDatasource?.datasourceType ?? EDatasourceType.CSharpFunctions,
      code: form.CSharpCode
    } as IDatasourceDto;

    if (this.props.editingDatasource?.id) {
      ds.id = this.props.editingDatasource.id;
    }

    await DatasourceService.SaveDatasource(ds)
      .then(async (response): Promise<void> => {
        if (response.status !== HttpStatusCode.Ok) {
          // A resolved response that is not 200: the trailing reset used to rescue this branch.
          this.saveStatus.fail();
          return;
        }
        this.props.sd.refreshActions();
        await this.props.sd.refreshDatabases();
        await LanguageServerService.ClearCache();
        this.props.sd.setDatasourceValidationErrors([]);
        this.props.sd.setWarningPanelType(WarningPanelType.Close);
        // The ✓ goes up first and the editor leaves after it — see `SaveStatus`.
        // A close confirmation this save answered ("Update & close") steps aside at once, so
        // the ✓ on the button is what is seen before the editor leaves.
        this.props.setShowModal(false);
        this.saveStatus.succeed((): void => {
          // The list behind the editor lights the row up; the button has already said "Saved".
          requestDatasourceReveal({ name: ds.name });
          this.closeDataSource();
        });
      })
      .catch((err): void => {
        this.saveStatus.fail();
        const compilerErrors: string[] | undefined = err.response?.data?.compilerErrors;
        if (compilerErrors && compilerErrors.length > 0) {
          // Toast only — a failed save must not linger as a phantom footer error.
          KMainFunctions.displayCompilerErrors(compilerErrors);
          return;
        }
        KMainFunctions.displayWarningNotification(err.response?.data?.message ?? err.message);
      });
  };

  // antd blocks onFinish (saveDatasource) when a Form.Item rule rejects. The CSharpCode error
  // renders below the full-height Monaco editor and is easy to miss, so the CREATE button looks
  // dead. Surface the validation messages as a notification so the failure is always visible.
  handleFinishFailed = ({ errorFields }: ValidateErrorEntity<ICsharpActionFormValues>): void => {
    const uniqueMessages: string[] = Array.from(new Set(errorFields.flatMap((field) => field.errors)));
    if (uniqueMessages.length === 0) return;
    const message: string =
      uniqueMessages.length === 1 ? uniqueMessages[0] : uniqueMessages.map((m) => `• ${m}`).join("\n");
    KMainFunctions.displayWarningNotification(message);
  };

  /**
   * What "unsaved" is measured against. Without arguments it reads what the editor holds now;
   * the seeding paths pass the values they have just written, because the name's `setState`
   * has not landed when they take the baseline.
   */
  getObjectHash(
    name: string = this.state.datasourceName,
    code: unknown = this.formRef.current?.getFieldValue("CSharpCode")
  ): string {
    return ObjectHash({ DatasourceName: name, CSharpCode: code });
  }

  handleStartRename = (): void => {
    this.formRef.current?.setFieldsValue({ DatasourceName: this.state.datasourceName });
    this.setState({ isNameOnEdit: true });
  };

  /** Commits the typed name once its rules — the async uniqueness check among them — pass. */
  handleConfirmRename = async (): Promise<string | undefined> => {
    const name: string | undefined = await validateRenameField(this.formRef.current, "DatasourceName");
    if (name === undefined) return undefined;
    this.setState({ datasourceName: name, isNameOnEdit: false });
    return name;
  };

  handleAbortRename = (): void => {
    this.formRef.current?.setFieldsValue({ DatasourceName: this.state.datasourceName });
    this.setState({ isNameOnEdit: false });
  };

  /** The name a save writes: the committed one, or — mid-rename — the typed one once it passes. */
  resolveNameForSave = async (): Promise<string | undefined> => {
    if (this.state.isNameOnEdit) return this.handleConfirmRename();
    if (!this.state.datasourceName) {
      KMainFunctions.displayWarningNotification("Please enter a Datasource Name");
      return undefined;
    }
    return this.state.datasourceName;
  };

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
    this.props.sd.clearCSharpTempValues();
    this.props.sd.setActiveCreatingDatasourceName(undefined, undefined);
  }

  handleCloseDatasource() {
    if (this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }

    this.props.setShowModal(true);
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.aiPendingEdit.dispose();
    this.controls.dispose();
    this.codeThemeObserver?.disconnect();

    if (this.completionProviderDisposable) {
      this.completionProviderDisposable.dispose();
      this.completionProviderDisposable = null;
    }
    if (this.hoverProviderDisposable) {
      this.hoverProviderDisposable.dispose();
      this.hoverProviderDisposable = null;
    }
    if (this.codeActionProviderDisposable) {
      this.codeActionProviderDisposable.dispose();
      this.codeActionProviderDisposable = null;
    }

    if (this.definitionProviderDisposable) {
      this.definitionProviderDisposable.dispose();
      this.definitionProviderDisposable = null;
    }
    // The Shiki highlighter is a shared app-lifetime singleton (see getCSharpHighlighter) —
    // deliberately NOT disposed here: Monaco's globally registered tokens provider still
    // references it, and disposing threw "Shiki instance has been disposed" on the next
    // tokenization after a remount. Just drop this component's reference.
    this.highlighter = null;
    // Release the Monaco form ref published on window in componentDidMount
    // (write-only global; nothing reads it) so it isn't retained after unmount.
    try {
      delete (window as unknown as { editorForm?: unknown }).editorForm;
    } catch {}
    this.abortPendingRequests();
  }

  abortPendingRequests = () => {
    if (this.diagnosticsAbortController) {
      this.diagnosticsAbortController.abort();
      this.diagnosticsAbortController = null;
    }
    if (this.codeActionsAbortController) {
      this.codeActionsAbortController.abort();
      this.codeActionsAbortController = null;
    }
  };

  editorBeforeMount = async (monaco: Monaco) => {
    let completionTimeout: NodeJS.Timeout;
    let hoverTimeout: NodeJS.Timeout;

    monaco.languages.register({ id: "csharp" });

    if (this.highlighter) {
      installShikiThemes(monaco, this.highlighter);
    } else {
      // A fast editor mount can beat componentDidMount's await — attach highlighting when ready
      // instead of silently skipping it for the whole session.
      const highlighter: Highlighter = await getCSharpHighlighter();
      installShikiThemes(monaco, highlighter);
    }

    injectMonacoStyleFixes();

    this.hoverProviderDisposable = monaco.languages.registerHoverProvider("csharp", {
      provideHover: async (model, position) => {
        if (!model) return null;

        try {
          if (hoverTimeout) {
            clearTimeout(hoverTimeout);
          }

          return await new Promise((resolve) => {
            hoverTimeout = setTimeout(async () => {
              const cacheKey = this.getCacheKey(model.getValue(), model.getOffsetAt(position));
              const cached = CreateConnectionCsharp.hoverCache.get(cacheKey);

              if (cached) {
                resolve(cached.info);
                return;
              }

              const hoverInfo = await LanguageServerService.GetHoverInfo(
                model.getValue(),
                model.getOffsetAt(position),
                this.props.editingDatasource?.id ?? null
              );

              if (!hoverInfo) {
                CreateConnectionCsharp.hoverCache.set(cacheKey, {
                  info: null,
                  timestamp: Date.now()
                });
                resolve(null);
                return;
              }

              const result = {
                range: {
                  startLineNumber: hoverInfo.startLineNumber + 1,
                  startColumn: hoverInfo.startColumn + 1,
                  endLineNumber: hoverInfo.endLineNumber + 1,
                  endColumn: hoverInfo.endColumn + 1
                },
                contents: [
                  {
                    value: hoverInfo.content,
                    isTrusted: true,
                    supportThemeIcons: true
                  }
                ]
              };

              CreateConnectionCsharp.hoverCache.set(cacheKey, {
                info: result,
                timestamp: Date.now()
              });

              resolve(result);
            }, 250);
          });
        } catch (error) {
          console.error("LSP hover error:", error);
          return null;
        }
      }
    });

    this.completionProviderDisposable = monaco.languages.registerCompletionItemProvider("csharp", {
      triggerCharacters: ["."],
      provideCompletionItems: async (model, position) => {
        if (!model) {
          return { suggestions: [] };
        }

        try {
          if (completionTimeout) {
            clearTimeout(completionTimeout);
          }

          return await new Promise((resolve) => {
            completionTimeout = setTimeout(async () => {
              const completions = await LanguageServerService.GetCompletions(
                model.getValue(),
                model.getOffsetAt(position),
                this.props.editingDatasource?.id ?? null
              );

              const word = model.getWordUntilPosition(position);
              const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn
              };

              resolve({
                suggestions: completions.map((item) => {
                  const kind = this.mapCompletionKindToMonaco(item.kind);
                  return {
                    label: item.label,
                    kind,
                    insertText: item.insertText.endsWith("<>")
                      ? `${item.insertText.slice(0, -2)}<$0>`
                      : item.insertText,
                    detail: this.getCompletionKindText(item.kind),
                    range,
                    filterText: item.label,
                    sortText: item.label,
                    documentation: {
                      value: item.detail
                    },
                    insertTextRules: item.insertText.endsWith("<>")
                      ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
                      : undefined
                  };
                })
              });
            }, 250); // 250ms debounce
          });
        } catch (error) {
          console.error("LSP completion error:", error);
          return { suggestions: [] };
        }
      }
    });

    this.codeActionProviderDisposable = monaco.languages.registerCodeActionProvider("csharp", {
      provideCodeActions: async (model, range, context, token): Promise<monaco.languages.CodeActionList> => {
        if (token.isCancellationRequested) {
          return { actions: [], dispose: () => {} };
        }
        const offset =
          context.markers && context.markers.length > 0
            ? model.getOffsetAt({
                lineNumber: context.markers[0].startLineNumber,
                column: context.markers[0].startColumn
              })
            : model.getOffsetAt(range.getStartPosition());
        const currentContent = model.getValue();
        let codeActions;
        if (currentContent === this.lastCodeContent && this.lastCodeActions.length > 0) {
          codeActions = this.lastCodeActions;
        } else {
          try {
            codeActions = await LanguageServerService.GetCodeActions(
              currentContent,
              this.props.editingDatasource?.id ?? null,
              offset
            );
            this.lastCodeContent = currentContent;
            this.lastCodeActions = codeActions;
          } catch (error) {
            console.error("LSP code actions error:", error);
            return { actions: [], dispose: () => {} };
          }
        }
        const pos = model.getPositionAt(offset);
        const filteredActions = codeActions.filter((action) => {
          const actionStartPos = {
            lineNumber: action.startLineNumber + 1,
            column: action.startColumn + 1
          };
          const actionEndPos = {
            lineNumber: action.endLineNumber + 1,
            column: action.endColumn + 1
          };

          if (action.title === "Remove unused using directive") {
            return pos.lineNumber === actionStartPos.lineNumber;
          }

          return (
            pos.lineNumber === actionStartPos.lineNumber &&
            pos.column >= actionStartPos.column &&
            pos.column <= actionEndPos.column
          );
        });
        return {
          actions: filteredActions.map((action) => {
            const lineNumber = action.startLineNumber + 1;
            const currentLine = model.getLineContent(lineNumber);
            const indentation = currentLine.match(/^\s*/)?.[0] || "";
            const editedCode = action.editedCode ? indentation + action.editedCode.trim() : "";

            return {
              title: action.title,
              kind: "quickfix",
              diagnostics: [],
              isPreferred: true,
              edit: {
                edits: [
                  {
                    resource: model.uri,
                    versionId: model.getVersionId(),
                    textEdit: {
                      range: {
                        startLineNumber: lineNumber,
                        startColumn: 1,
                        endLineNumber: lineNumber,
                        endColumn: model.getLineMaxColumn(lineNumber)
                      },
                      text: editedCode
                    }
                  }
                ]
              }
            };
          }),
          dispose: () => {}
        };
      }
    });

    if (this.editorRef) {
      this.editorRef.addAction({
        id: "applyCodeAction",
        label: "Apply Code Action",
        run: async (editor) => {
          const model = editor.getModel();
          if (!model) return;
          const viewState = editor.saveViewState();
          const position = editor.getPosition();
          const quickFixAction = editor.getAction("editor.action.quickFix");
          if (quickFixAction) {
            await quickFixAction.run();
          }
          this.formRef.current?.setFieldsValue({ CSharpCode: model.getValue() });
          setTimeout(() => {
            if (viewState) editor.restoreViewState(viewState);
            if (position) {
              editor.setPosition(position);
              editor.revealPosition(position);
            }
          }, 0);
        }
      });
    }

    this.definitionProviderDisposable = monaco.languages.registerDefinitionProvider("csharp", {
      provideDefinition: async (model, position) => {
        try {
          if (
            this.lastDefinitionRequest &&
            this.lastDefinitionRequest.position.lineNumber === position.lineNumber &&
            this.lastDefinitionRequest.position.column === position.column
          ) {
            return await this.lastDefinitionRequest.promise;
          }

          const promise = new Promise((resolve) => {
            const cacheKey = this.getCacheKey(model.getValue(), model.getOffsetAt(position));
            const cached = CreateConnectionCsharp.definitionCache.get(cacheKey);

            if (cached) {
              resolve(cached.info);
              return;
            }

            LanguageServerService.GetDefinition(
              model.getValue(),
              model.getOffsetAt(position),
              this.props.editingDatasource?.id ?? null
            )
              .then((definitionInfo) => {
                if (!definitionInfo) {
                  resolve(null);
                  return;
                }

                const result = {
                  uri: model.uri,
                  range: {
                    startLineNumber: definitionInfo.startLineNumber + 1,
                    startColumn: definitionInfo.startColumn + 1,
                    endLineNumber: definitionInfo.endLineNumber + 1,
                    endColumn: definitionInfo.endColumn + 1
                  }
                };

                CreateConnectionCsharp.definitionCache.set(cacheKey, {
                  info: result,
                  timestamp: Date.now()
                });

                resolve(result);
              })
              .catch(() => {
                resolve(null);
              });
          });

          this.lastDefinitionRequest = { position, promise };

          return await promise;
        } catch (error) {
          console.error("LSP definition error:", error);
          return null;
        } finally {
          this.lastDefinitionRequest = null;
        }
      }
    });
  };

  mapCompletionKindToMonaco = (kind: CompletionItemKind) => {
    return kind === CompletionItemKind.Method
      ? 2 // Method
      : kind === CompletionItemKind.Function
        ? 3 // Function
        : kind === CompletionItemKind.Constructor
          ? 4 // Constructor
          : kind === CompletionItemKind.Field
            ? 5 // Field
            : kind === CompletionItemKind.Variable
              ? 6 // Variable
              : kind === CompletionItemKind.Class
                ? 7 // Class
                : kind === CompletionItemKind.Interface
                  ? 8 // Interface
                  : kind === CompletionItemKind.Module
                    ? 9 // Module
                    : kind === CompletionItemKind.Property
                      ? 10 // Property
                      : kind === CompletionItemKind.Unit
                        ? 11 // Unit
                        : kind === CompletionItemKind.Value
                          ? 12 // Value
                          : kind === CompletionItemKind.Enum
                            ? 13 // Enum
                            : kind === CompletionItemKind.Keyword
                              ? 14 // Keyword
                              : kind === CompletionItemKind.Snippet
                                ? 15 // Snippet
                                : kind === CompletionItemKind.Color
                                  ? 16 // Color
                                  : kind === CompletionItemKind.File
                                    ? 17 // File
                                    : kind === CompletionItemKind.Reference
                                      ? 18 // Reference
                                      : kind === CompletionItemKind.Folder
                                        ? 19 // Folder
                                        : kind === CompletionItemKind.EnumMember
                                          ? 20 // EnumMember
                                          : kind === CompletionItemKind.Constant
                                            ? 21 // Constant
                                            : kind === CompletionItemKind.Struct
                                              ? 22 // Struct
                                              : kind === CompletionItemKind.Event
                                                ? 23 // Event
                                                : kind === CompletionItemKind.Operator
                                                  ? 24 // Operator
                                                  : kind === CompletionItemKind.TypeParameter
                                                    ? 25 // TypeParameter
                                                    : 1; // Default to Text
  };

  getCompletionKindText = (kind: CompletionItemKind): string => {
    switch (kind) {
      case CompletionItemKind.Method:
        return "method";
      case CompletionItemKind.Function:
        return "function";
      case CompletionItemKind.Constructor:
        return "constructor";
      case CompletionItemKind.Field:
        return "field";
      case CompletionItemKind.Variable:
        return "variable";
      case CompletionItemKind.Class:
        return "class";
      case CompletionItemKind.Interface:
        return "interface";
      case CompletionItemKind.Module:
        return "namespace";
      case CompletionItemKind.Property:
        return "property";
      case CompletionItemKind.Unit:
        return "unit";
      case CompletionItemKind.Value:
        return "value";
      case CompletionItemKind.Enum:
        return "enum";
      case CompletionItemKind.Keyword:
        return "keyword";
      case CompletionItemKind.Snippet:
        return "snippet";
      case CompletionItemKind.Color:
        return "color";
      case CompletionItemKind.File:
        return "file";
      case CompletionItemKind.Reference:
        return "reference";
      case CompletionItemKind.Folder:
        return "folder";
      case CompletionItemKind.EnumMember:
        return "enum member";
      case CompletionItemKind.Constant:
        return "constant";
      case CompletionItemKind.Struct:
        return "struct";
      case CompletionItemKind.Event:
        return "event";
      case CompletionItemKind.Operator:
        return "operator";
      case CompletionItemKind.TypeParameter:
        return "type parameter";
      default:
        return "";
    }
  };

  handleEditorChange = debounce(async (value: string | undefined) => {
    if (!value) return;

    this.clearCaches();
    LanguageServerService.clearCodeActionsCache();

    if (this.editorRef) {
      const model = this.editorRef.getModel();
      if (!model) return;

      const monaco = (window as any).monaco;
      if (!monaco) return;

      this.abortPendingRequests();

      this.diagnosticsAbortController = new AbortController();
      this.codeActionsAbortController = new AbortController();

      try {
        const diagnostics = await LanguageServerService.GetDiagnostics(
          value,
          this.props.editingDatasource?.id ?? null,
          this.diagnosticsAbortController.signal
        );
        if (this.diagnosticsAbortController?.signal.aborted) return;

        this.setState({ diagnostics });

        const markers = diagnostics.map((d) => {
          return {
            severity: this.mapDiagnosticSeverity(d.severity, monaco),
            startLineNumber: d.startLineNumber + 1,
            startColumn: d.startColumn + 1,
            endLineNumber: d.endLineNumber + 1,
            endColumn: d.endColumn + 1,
            message: d.message,
            source: "Kuika Language Services"
          };
        });

        monaco.editor.setModelMarkers(model, "csharp", markers);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error("LSP diagnostics error:", error);
        }
      }

      try {
        const position = this.editorRef?.getPosition();
        const model = this.editorRef?.getModel();
        const offset = position && model ? model.getOffsetAt(position) : undefined;
        const codeActions = await LanguageServerService.GetCodeActions(
          value,
          this.props.editingDatasource?.id ?? null,
          offset,
          this.codeActionsAbortController.signal
        );
        if (this.codeActionsAbortController?.signal.aborted) return;

        this.lastCodeActions = codeActions;
        this.lastCodeContent = value;

        monaco.editor.setModelMarkers(model, "codeActions", []);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error("LSP code actions error:", error);
        }
        monaco.editor.setModelMarkers(model, "codeActions", []);
      }
    }
  }, 500);

  mapDiagnosticSeverity = (severity: number, monaco: any) => {
    switch (severity) {
      case 3:
        return monaco.MarkerSeverity.Error; // 8
      case 2:
        return monaco.MarkerSeverity.Warning; // 4
      case 1:
        return monaco.MarkerSeverity.Info; // 2
      case 0:
        return monaco.MarkerSeverity.Hint; // 1
      default:
        return monaco.MarkerSeverity.Info;
    }
  };

  onTemplateChange = async (value: any) => {
    // The backend derives the template's class name from the datasource name (all template types,
    // not just CSharpFunctions) — the old client-side NewClassName regex is gone.
    const response = await DatasourceService.GetCSharpCodeTemplates(value, this.state.datasourceName);
    if (response.status === HttpStatusCode.Ok) {
      const code = response.data as string;
      const fieldsValue = this.formRef.current?.getFieldsValue();
      fieldsValue.CSharpCode = `${code}`;
      this.formRef.current?.setFieldsValue(fieldsValue);
      this.setState({ isTemplateSelected: true, selectedTemplate: value });
    }
  };

  /**
   * The header is too narrow for the longer template names, so the picker cuts them with an
   * ellipsis. Measured on hover — cheap, exact, and no observer to dispose.
   */
  measureTemplateLabel = (): void => {
    const label: Element | null | undefined =
      this.templateSelectRef.current?.querySelector(".rc-select-selection-item");
    const isCut: boolean = !!label && label.scrollWidth > label.clientWidth;
    const truncatedTemplateLabel: string = isCut ? (label?.textContent ?? "") : "";
    if (truncatedTemplateLabel !== this.state.truncatedTemplateLabel) {
      this.setState({ truncatedTemplateLabel });
    }
  };

  setSubmitButtonDisabledStatus() {
    if (!this.formRef.current) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }

    if (!this.formRef.current.getFieldValue("CSharpCode")) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }

    if (this.objectHash === this.getObjectHash()) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }
    this.setState({ submitButtonDisabledStatus: false });
  }

  handleEditorMount = (editor: monacoEditor.editor.IStandaloneCodeEditor) => {
    this.editorRef = editor;
    this.aiPendingEdit.attach(monacoEditor, editor);
    this.controls.attach(monacoEditor, editor);
    // Shiki registers its own themes, so this editor switches between them rather than
    // using the shell's `kuika-code`.
    this.codeThemeObserver = watchAppTheme((theme: TAppTheme): void => {
      const shikiTheme: string = theme === "dark" ? "dark-plus" : "light-plus";
      this.setState({ shikiTheme });
      monacoEditor.editor.setTheme(shikiTheme);
    });

    editor.onMouseUp((event) => {
      const position = event.target.position;
      if (position) {
        this.lastClickedRow = position.lineNumber;
      }
    });

    editor.onDidChangeModelContent(() => {
      const model = editor.getModel();
      if (model) {
        this.formRef.current?.setFieldsValue({
          CSharpCode: model.getValue()
        });
      }
    });

    const model = editor.getModel();
    if (model) {
      const value = model.getValue();
      this.handleEditorChange(value);
    }
  };

  /** The editor's own content listener writes the form field and refreshes diagnostics. */
  handleApplyPendingAiEdit = () => {
    this.aiPendingEdit.commit();
  };

  handlePasteToLastClickedRow = () => {
    const editor = this.editorRef;
    const lastClickedRow = this.lastClickedRow;

    if (editor && lastClickedRow) {
      navigator.clipboard
        .readText()
        .then((text) => {
          editor.trigger("keyboard", "type", { text });
        })
        .catch((error) => {
          KMainFunctions.exceptionHandler(error);
        });
    }
  };

  handleFormatCode = async () => {
    const editor = this.editorRef;
    if (!editor) return;

    const model = editor.getModel();
    if (!model) return;

    const sourceCode = model.getValue();
    const formattedCode = await LanguageServerService.Format(sourceCode);

    if (formattedCode) {
      editor.executeEdits("format", [
        {
          range: model.getFullModelRange(),
          text: formattedCode
        }
      ]);

      this.formRef.current?.setFieldsValue({
        CSharpCode: formattedCode
      });

      this.controls.markFormatted();
    }
  };

  handleLibrarySearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    this.setState({
      librarySearchQuery: e.target.value
    });
  };

  toggleLibraryView = (view: "installed" | "new") => {
    this.setState({ libraryView: view }, () => {
      if (view === "new") {
        this.fetchNugetPackages();
      } else if (view === "installed") {
        this.fetchInstalledLibraries();
      }
    });
  };

  handleNugetSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    this.setState({ nugetSearchQuery: value });
    this.debouncedFetchNugetPackages(value);
  };

  debouncedFetchNugetPackages = debounce((searchQuery: string) => {
    this.setState(
      {
        nugetPagination: {
          ...this.state.nugetPagination,
          skip: 0
        }
      },
      () => this.fetchNugetPackages()
    );
  }, 500);

  fetchNugetPackages = async () => {
    try {
      this.setState({ isLoadingNugetPackages: true });
      const { nugetSearchQuery, nugetPagination } = this.state;
      const response = await PackageManagementService.ListNuGetPackages(
        nugetSearchQuery,
        nugetPagination.skip,
        nugetPagination.take
      );

      if (response && response.status === HttpStatusCode.Ok) {
        const newPackages = response.data.data.map((pkg) => ({
          id: pkg.id,
          version: pkg.version,
          description: pkg.description
        }));

        this.setState({
          nugetPackages: nugetPagination.skip > 0 ? [...this.state.nugetPackages, ...newPackages] : newPackages,
          nugetPagination: {
            ...this.state.nugetPagination,
            total: response.data.totalHits
          }
        });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingNugetPackages: false });
    }
  };

  handleLoadMoreNugetPackages = () => {
    const { nugetPagination } = this.state;
    this.setState(
      {
        nugetPagination: {
          ...nugetPagination,
          skip: nugetPagination.skip + nugetPagination.take
        }
      },
      this.fetchNugetPackages
    );
  };

  addLibrary = async (packageId: string, version: string) => {
    try {
      this.setState({
        activePackageLoading: packageId
      });

      if (this.props.sd.project?.id) {
        const request: ICreateLibraryRequest = {
          name: packageId,
          version,
          packageType: PackageType.Nuget,
          isActive: true,
          kPackageRepository_Id: null
        };

        const response = await PackageManagementService.CreateLibrary(request);

        if (response && response.status === HttpStatusCode.Created) {
          this.fetchInstalledLibraries();
          await LanguageServerService.ClearCache();
          KMainFunctions.displaySuccessNotification(`Library ${packageId} added`);
          const installWarnings: string[] = response.data?.warnings ?? [];
          installWarnings.forEach((warning: string): void => {
            KMainFunctions.displayWarningNotification(warning);
          });
        }
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({
        activePackageLoading: undefined
      });
    }
  };

  fetchInstalledLibraries = async () => {
    try {
      this.setState({ isLoadingInstalledLibraries: true });

      if (this.props.sd.project?.id) {
        const response = await PackageManagementService.GetLibrariesByProject(
          this.props.sd.project.id,
          PackageType.Nuget
        );

        if (response && response.status === HttpStatusCode.Ok) {
          this.setState({
            installedLibraries: response.data.map((lib) => {
              const library: {
                id: string;
                name: string;
                version: string;
                description?: string;
              } = {
                id: lib.id || "",
                name: lib.name || "",
                version: lib.version || "1.0.0"
              };

              if ("description" in lib && lib.description) {
                library.description = lib.description as string;
              }

              return library;
            })
          });
        }
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingInstalledLibraries: false });
    }
  };

  deleteInstalledLibrary = async (libraryId: string, libraryName: string) => {
    try {
      this.setState({ isLoadingInstalledLibraries: true });

      const response = await PackageManagementService.DeleteLibrary(libraryId);

      if (response && response.status === HttpStatusCode.NoContent) {
        await this.fetchInstalledLibraries();
        await LanguageServerService.ClearCache();
        KMainFunctions.displaySuccessNotification(`Library ${libraryName} removed`);
      } else {
        KMainFunctions.displayErrorNotification(`Failed to remove library ${libraryName}`);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingInstalledLibraries: false });
    }
  };

  toggleLibraryExpansion = async (packageId: string) => {
    const { expandedLibrary, libraryVersions } = this.state;

    if (expandedLibrary === packageId) {
      this.setState({ expandedLibrary: undefined });
      return;
    }

    this.setState({ expandedLibrary: packageId });

    if (libraryVersions[packageId] && libraryVersions[packageId].length > 0) {
      return;
    }

    await this.fetchPackageVersions(packageId);
  };

  fetchPackageVersions = async (packageId: string) => {
    try {
      this.setState({ isLoadingVersions: true });

      const response = await PackageManagementService.GetNuGetPackageDetails(packageId);

      if (response && response.status === HttpStatusCode.Ok && response.data.versions) {
        this.setState((prevState) => ({
          libraryVersions: {
            ...prevState.libraryVersions,
            [packageId]: response.data.versions
          }
        }));
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingVersions: false });
    }
  };

  // Confirmation happens inline in the popover; this performs the update directly.
  changeLibraryVersion = async (
    libraryId: string,
    libraryName: string,
    _currentVersion: string,
    newVersion: string
  ): Promise<void> => {
    try {
      this.setState({ isLoadingInstalledLibraries: true });

      const libraryToUpdate = this.state.installedLibraries.find((lib) => lib.id === libraryId);
      if (!libraryToUpdate) {
        throw new Error("Library not found");
      }

      const updateRequest: IUpdateLibraryRequest = {
        name: libraryToUpdate.name,
        version: newVersion,
        packageType: PackageType.Nuget,
        isActive: true,
        kPackageRepository_Id: null
      };

      const response = await PackageManagementService.UpdateLibrary(libraryId, updateRequest);

      if (response && response.status === HttpStatusCode.NoContent) {
        await this.fetchInstalledLibraries();
        await LanguageServerService.ClearCache();
        KMainFunctions.displaySuccessNotification(`Library ${libraryName} updated to ${newVersion}`);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingInstalledLibraries: false });
    }
  };

  handleParseMethods = async (): Promise<ITestRunnerMethod[]> => {
    const code: string = this.formRef.current?.getFieldValue("CSharpCode") || "";
    const response = await DatasourceService.ParseCSharp(code);
    return response.data.methods || [];
  };

  handleExecute = async (methodName: string, args: unknown[], _serverSide: boolean): Promise<string> => {
    const code: string = this.formRef.current?.getFieldValue("CSharpCode") || "";
    const response = await DatasourceService.ExecuteCSharp({
      code,
      methodName,
      args,
      projectId: this.props.sd.project?.id
    });
    const data = response.data;
    if (data.ok) {
      return JSON.stringify(data.result, null, 2) ?? "undefined";
    }
    return `${data.errorType ?? "Error"}: ${data.errorMessage ?? "unknown error"}`;
  };

  render() {
    this.setSubmitButtonDisabledStatus();
    const filteredLibraries = this.state.installedLibraries.filter((lib) =>
      lib.name.toLowerCase().includes(this.state.librarySearchQuery.toLowerCase())
    );
    // The dimming overlay and the close-confirmation dialog ask the same question the button's
    // status answers; they lift while the ✓ is still up, which is when the editor is leaving anyway.
    const isSaving: boolean = this.state.saveStatus === "loading";

    return (
      <Form
        name="dsModal"
        ref={this.formRef}
        onFinish={this.saveDatasource}
        onFinishFailed={this.handleFinishFailed}
        onValuesChange={() => {
          this.setSubmitButtonDisabledStatus();
        }}
        layout="vertical"
        initialValues={{
          DatasourceName: this.props.editingDatasource?.name ? this.props.editingDatasource.name : "",
          CSharpCode: ``
        }}
        style={{
          height: "100%"
        }}
      >
        <KuikaSpinnerSmall spinning={isSaving}>
          <div className="kuika__code-editor__page">
            <div className="kuika__code-editor__shell">
              <CodeEditorHeader
                title={this.state.datasourceName || "C# Action"}
                renderTitle={(): React.ReactNode => (
                  <CodeEditorRenameTitle
                    identity={
                      <CodeDatasourceSwitcherChip
                        sd={this.props.sd}
                        setNextCallbackFunction={this.props.setNextCallbackFunction}
                        types={CSHARP_EDITOR_DATASOURCE_TYPES}
                        current={this.props.editingDatasource}
                        name={this.state.datasourceName}
                        entityLabel="C# Action"
                        // The template picked is what the file is, even before it is saved.
                        datasourceType={
                          this.state.selectedTemplate ??
                          this.props.editingDatasource?.datasourceType ??
                          EDatasourceType.CSharpFunctions
                        }
                        triggerTestId="csharpActionSwitcherTrigger"
                        labelTestId="csharpActionNameLabel"
                        onManageAll={() => this.handleCloseDatasource()}
                      />
                    }
                    isRenaming={this.state.isNameOnEdit}
                    canAbortRename={!!this.state.datasourceName}
                    entityLabel="C# action"
                    fieldName="DatasourceName"
                    rules={[
                      { required: true, message: "Please enter a Datasource Name" },
                      { validator: this.nameValidator }
                    ]}
                    placeholder="Datasource name"
                    testIds={codeEditorRenameTestIds("csharpActionName")}
                    onStartRename={this.handleStartRename}
                    onConfirmRename={(): void => void this.handleConfirmRename()}
                    onAbortRename={this.handleAbortRename}
                  />
                )}
                // What this datasource IS, so it belongs beside its name rather than on the
                // toolbar with the editor's own controls.
                status={
                  <KuikaTooltip content={this.state.truncatedTemplateLabel || undefined} position="bottom" keepMounted>
                    <span
                      className="kuika__code-editor__header__select"
                      ref={this.templateSelectRef}
                      onMouseEnter={this.measureTemplateLabel}
                    >
                      <KuikaSelectbox
                        placeholder="Select a Template"
                        onChange={this.onTemplateChange}
                        defaultValue={this.props.sd.activeCreatingCSharpType ?? EDatasourceType.CSharpFunctions}
                        value={this.state.selectedTemplate}
                        options={getCSharpTemplateOptionsFor(this.props.editingDatasource?.datasourceType)}
                        overlayClassName="kuika__code-editor__header__select-dropdown"
                        data-testid="csharpActionTypeSelect"
                      />
                    </span>
                  </KuikaTooltip>
                }
                backLabel="Back to Datasources"
                saveLabel={this.props.editingDatasource ? "UPDATE" : "CREATE"}
                saveStatus={this.state.saveStatus}
                isSaveDisabled={this.state.submitButtonDisabledStatus}
                // Nothing to create until a template is chosen; editing always has one.
                isSaveHidden={!this.state.isTemplateSelected && !this.props.editingDatasource}
                saveTestId="csharpActionCreateUpdateButton"
                onClose={() => this.handleCloseDatasource()}
                closeTestId="closeCreateActionPanel"
              />

              <div className="kuika__code-editor__body">
                <div className="kuika__code-editor__main">
                  <CodeEditorToolbar
                    {...this.controls.toolbarBinding}
                    onFormat={this.handleFormatCode}
                    formatHint={
                      <div>
                        <p>How TIDY UP Formats Your Code:</p>
                        <ul style={{ paddingLeft: "16px", margin: "4px 0" }}>
                          <li>Wraps code at 120th character (including spaces)</li>
                          <li>Uses 4 spaces for indentation</li>
                          <li>Ensures consistent brace placement</li>
                          <li>Adds proper spacing around operators</li>
                          <li>Organizes using statements</li>
                          <li>Standardizes method declaration spacing</li>
                          <li>And more...</li>
                        </ul>

                        {this.props.editingDatasource && (
                          <p style={{ marginTop: "8px" }}>
                            Formatting changes become permanent after you click UPDATE.
                          </p>
                        )}
                      </div>
                    }
                    trailing={
                      <ExternalLibrariesButton
                        libraries={this.state.installedLibraries}
                        packages={this.state.nugetPackages}
                        versions={
                          this.state.expandedLibrary
                            ? this.state.libraryVersions[this.state.expandedLibrary] || EMPTY_VERSIONS
                            : EMPTY_VERSIONS
                        }
                        hasMorePackages={
                          this.state.nugetPagination.skip + this.state.nugetPagination.take <
                          this.state.nugetPagination.total
                        }
                        onOpen={() => this.fetchInstalledLibraries()}
                        onSearch={(query, view) => {
                          if (view === "installed") this.fetchInstalledLibraries();
                          else {
                            this.setState(
                              { nugetSearchQuery: query, nugetPagination: { ...this.state.nugetPagination, skip: 0 } },
                              this.fetchNugetPackages
                            );
                          }
                        }}
                        onExpand={this.toggleLibraryExpansion}
                        onAdd={this.addLibrary}
                        onDelete={this.deleteInstalledLibrary}
                        onChangeVersion={this.changeLibraryVersion}
                        onLoadMore={this.handleLoadMoreNugetPackages}
                      />
                    }
                  />

                  {this.state.pendingAiEdit && (
                    <AiPendingEditBar
                      plan={this.state.pendingAiEdit}
                      subject="code"
                      onApply={this.handleApplyPendingAiEdit}
                      onDiscard={this.aiPendingEdit.discard}
                      onChangeMode={this.aiPendingEdit.setMode}
                    />
                  )}

                  {(this.state.isTemplateSelected || this.props.editingDatasource) && (
                    <div
                      className="kuika__code-editor__editor"
                      data-testid="csharpCodeEditor"
                      ref={this.editorContainerRef}
                    >
                      <Form.Item
                        name="CSharpCode"
                        rules={[
                          { required: true, message: "Please enter a C# code!" },
                          { validator: this.namespaceValidator }
                        ]}
                        noStyle
                      >
                        <Editor
                          language="csharp"
                          theme={this.state.shikiTheme}
                          options={csharpEditorOptions as unknown as Parameters<typeof Editor>[0]["options"]}
                          onMount={this.handleEditorMount as unknown as Parameters<typeof Editor>[0]["onMount"]}
                          beforeMount={this.editorBeforeMount}
                          onChange={this.handleEditorChange}
                        />
                      </Form.Item>
                    </div>
                  )}

                  {(this.state.isTemplateSelected || this.props.editingDatasource) && (
                    <TestRunner
                      onParseMethods={this.handleParseMethods}
                      onExecute={this.handleExecute}
                      showServerClientSplit={false}
                      runButtonLabel="Run"
                      emptyMessage="No methods found. Write public static methods and open this panel."
                    />
                  )}
                </div>

                <CodeEditorSplitter
                  orientation="vertical"
                  size={this.state.sidePanelWidth}
                  onSizeChange={(sidePanelWidth) => this.setState({ sidePanelWidth })}
                  minSize={240}
                  maxSize={520}
                  isCollapsed={!this.state.isSidePanelOpened}
                  onToggleCollapsed={(isCollapsed) => this.setState({ isSidePanelOpened: !isCollapsed })}
                />

                {this.state.isSidePanelOpened && (
                  <CodeEditorSidePanel
                    width={this.state.sidePanelWidth}
                    activeTab={this.state.sideTab}
                    onTabChange={(sideTab) => this.setState({ sideTab })}
                    parameterCount={0}
                    coachContent={
                      <AICodeGenerator
                        type={EAICodeGeneratorType.CSharp}
                        datasourceId={this.props.editingDatasource?.id}
                        onApplyCode={this.aiPendingEdit.request}
                      />
                    }
                    // A method's arguments are typed into the test console, so there is no
                    // second pane to offer here.
                    parametersContent={null}
                  />
                )}
              </div>
            </div>
          </div>
        </KuikaSpinnerSmall>
        <CloseConfirmationModal
          itemName={this.state.datasourceName}
          closeType={"C# Connection"}
          triggerModal={this.props.showModal}
          isLoading={isSaving}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeDataSource();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </Form>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };

  public getCacheKey(code: string, offset: number): string {
    const model = this.editorRef?.getModel();
    if (!model) return "";

    const pos = model.getPositionAt(offset);
    const wordAtPosition = model.getWordAtPosition(pos);

    if (!wordAtPosition) return "";

    return `${ObjectHash(code)}_${pos.lineNumber}_${wordAtPosition.startColumn}_${wordAtPosition.endColumn}_${
      wordAtPosition.word
    }`;
  }

  public clearCaches() {
    CreateConnectionCsharp.hoverCache.clear();
    CreateConnectionCsharp.definitionCache.clear();
  }
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const createConnectionCsharp = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<ICreateConnectionCsharpProps>(CreateConnectionCsharp));
export { createConnectionCsharp as CreateConnectionCsharp };
